import { FormSection } from '../types/form';

export const formSections: { [key: string]: FormSection } = {
  initial: {
    id: 'initial',
    title: 'Initial Screening',
    fields: [
      {
        id: 'jurisdiction',
        type: 'select',
        question: 'Where is the property located?',
        options: ['England', 'Wales', 'Scotland', 'Northern Ireland'],
        validation: { required: true },
        description: 'The location determines which laws and regulations apply to your tenancy agreement. Different parts of the UK have different legal requirements.'
      },
      {
        id: 'tenantType',
        type: 'radio',
        question: 'Is the tenant an individual or a company?',
        options: ['Individual', 'Company'],
        validation: { required: true },
        description: 'Company lets have different legal requirements and protections compared to individual tenancies. This affects the type of agreement needed.',
        nextStep: {
          conditions: [
            { value: 'Company', goto: 'companyLet' }
          ],
          default: 'landlordResidence'
        }
      },
      {
        id: 'landlordResidence',
        type: 'radio',
        question: 'Will the landlord live in the same property as the tenant?',
        options: ['Yes', 'No'],
        validation: { required: true },
        description: 'If the landlord lives in the property, this creates a lodger arrangement (excluded tenancy) with different rights and obligations than a standard tenancy.',
        nextStep: {
          conditions: [
            { value: 'Yes', goto: 'excludedTenancy' }
          ],
          default: 'mainResidence'
        }
      },
      {
        id: 'mainResidence',
        type: 'radio',
        question: 'Will the tenant occupy the property as their only or main home?',
        options: ['Yes', 'No'],
        validation: { required: true },
        description: 'This determines if the tenancy qualifies as an Assured Shorthold Tenancy (AST). ASTs provide specific legal protections for tenants using the property as their primary residence.',
        nextStep: {
          conditions: [
            { value: 'Yes', goto: 'ast' },
            { value: 'No', goto: 'purposeCheck' }
          ]
        }
      }
    ]
  },
  ast: {
    id: 'ast',
    title: 'Assured Shorthold Tenancy (AST) Details',
    fields: [
      {
        id: 'landlordName',
        type: 'text',
        question: 'What is the landlord\'s full name?',
        validation: { required: true }
      },
      {
        id: 'landlordEmail',
        type: 'text',
        question: 'What is the landlord\'s email address?',
        validation: { 
          required: true,
          pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$'
        },
        description: 'This email will be used for document signing and communications.'
      },
      {
        id: 'landlordAddress',
        type: 'text',
        question: 'What is the landlord\'s correspondence address (physical location)?',
        validation: { required: true }
      },
      {
        id: 'tenantName',
        type: 'text',
        question: 'What is the tenant\'s full name?',
        validation: { required: true }
      },
      {
        id: 'tenantEmail',
        type: 'text',
        question: 'What is the tenant\'s email address?',
        validation: { 
          required: true,
          pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$'
        },
        description: 'This email will be used for document signing and communications.'
      },
      {
        id: 'tenantCurrentAddress',
        type: 'text',
        question: 'What is the tenant\'s current address (physical location)?',
        validation: { required: true }
      },
      {
        id: 'propertyAddress',
        type: 'text',
        question: 'What is the full address of the property being let (physical location)?',
        validation: { required: true }
      },
      {
        id: 'startDate',
        type: 'date',
        question: 'When does the tenancy begin?',
        validation: { required: true }
      },
      {
        id: 'tenancyDuration',
        type: 'select',
        question: 'What is the initial fixed term of the tenancy?',
        options: ['6 months', '12 months', '24 months', 'Other'],
        validation: { required: true }
      },
      {
        id: 'breakClause',
        type: 'select',
        question: 'Do you want to include a break clause?',
        options: [
          'No break clause',
          'Mutual break clause (both parties)',
          'Landlord-only break clause',
          'Tenant-only break clause'
        ],
        validation: { required: true },
        description: 'A break clause allows early termination of the tenancy before the fixed term ends. Mutual break clauses (recommended) allow both parties to end the tenancy, typically after 6 months with 2 months\' notice. Break clauses must be fair and comply with consumer protection laws. The clause will specify: when it can be activated (e.g., after 6 months), notice period required (typically 2 months), and how notice must be given (usually in writing). Without a break clause, the tenancy cannot be ended early unless both parties agree.',
        nextStep: {
          conditions: [
            { value: 'No break clause', goto: 'rentAmount' }
          ]
        }
      },
      {
        id: 'breakClauseTerms',
        type: 'select',
        question: 'When can the break clause be activated?',
        options: [
          'After 6 months with 2 months\' notice',
          'After 4 months with 2 months\' notice',
          'After 9 months with 3 months\' notice',
          'Custom terms'
        ],
        validation: {
          conditionalRequired: {
            dependsOn: 'breakClause',
            condition: { notValue: 'No break clause' }
          }
        },
        description: 'The most common arrangement is activation after 6 months with 2 months\' notice, meaning the earliest possible end date would be at month 6. Notice can be given at month 4 to end at month 6. All parties remain responsible for their obligations until the termination date.'
      },
      {
        id: 'breakClauseCustomTerms',
        type: 'text',
        question: 'Specify custom break clause terms:',
        validation: {
          conditionalRequired: {
            dependsOn: 'breakClauseTerms',
            condition: { value: 'Custom terms' }
          }
        },
        description: 'If selecting custom terms, specify: 1) When the break clause can be activated, 2) Notice period required, 3) How notice must be given, 4) Any special conditions that must be met. Ensure terms are fair and legally compliant.'
      },
      {
        id: 'rentAmount',
        type: 'number',
        question: 'What is the monthly rent amount? (in GBP)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'rentDueDate',
        type: 'select',
        question: 'On which day of the month is the rent due?',
        options: ['1st', '5th', '15th', '25th', 'Other'],
        validation: { required: true }
      },
      {
        id: 'depositAmount',
        type: 'number',
        question: 'What is the security deposit amount? (in GBP)',
        description: 'Maximum 5 weeks\' rent (or 6 weeks if annual rent exceeds £50,000)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'depositScheme',
        type: 'select',
        question: 'Which deposit protection scheme will be used?',
        options: [
          'Deposit Protection Service (DPS)',
          'MyDeposits',
          'Tenancy Deposit Scheme (TDS)'
        ],
        validation: { required: true }
      },
      {
        id: 'furnishingStatus',
        type: 'select',
        question: 'What is the furnishing status of the property?',
        options: ['Furnished', 'Part Furnished', 'Unfurnished'],
        validation: { required: true }
      },
      {
        id: 'utilities',
        type: 'checkbox',
        question: 'Which utilities are the tenant\'s responsibility?',
        options: ['Gas', 'Electricity', 'Water', 'Council Tax', 'Internet', 'TV License'],
        validation: { required: true }
      },
      {
        id: 'permitsPets',
        type: 'radio',
        question: 'Are pets allowed in the property?',
        options: ['Yes', 'No', 'Subject to Approval'],
        validation: { required: true }
      },
      {
        id: 'propertyDescription',
        type: 'text',
        question: 'Provide a detailed description of the property including any shared or exclusive areas:',
        validation: { required: true },
        description: 'Include details about rooms, garden, parking, storage, communal areas, etc.'
      },
      {
        id: 'isHMO',
        type: 'radio',
        question: 'Is the property a House in Multiple Occupation (HMO)?',
        options: ['Yes', 'No'],
        validation: { required: true },
        description: 'An HMO is a property rented out by at least 3 people who are not from 1 household but share facilities like the bathroom and kitchen.',
        nextStep: {
          conditions: [
            { value: 'No', goto: 'isJointTenancy' }
          ]
        }
      },
      {
        id: 'hmoLicenseNumber',
        type: 'text',
        question: 'What is the HMO license number?',
        validation: {
          conditionalRequired: {
            dependsOn: 'isHMO',
            condition: { value: 'Yes' }
          }
        },
        description: 'HMO properties require licensing from the local authority. Provide the license reference number.'
      },
      {
        id: 'isJointTenancy',
        type: 'radio',
        question: 'Is this a joint tenancy with multiple tenants?',
        options: ['Yes', 'No'],
        validation: { required: true }
      },
      {
        id: 'additionalTenantNames',
        type: 'text',
        question: 'List all additional tenant names (comma separated):',
        validation: {
          conditionalRequired: {
            dependsOn: 'isJointTenancy',
            condition: { value: 'Yes' }
          }
        },
        description: 'All joint tenants will be jointly and severally liable for the rent and tenancy obligations.'
      },
      {
        id: 'hasGuarantor',
        type: 'radio',
        question: 'Will there be a guarantor for this tenancy?',
        options: ['Yes', 'No'],
        validation: { required: true }
      },
      {
        id: 'guarantorName',
        type: 'text',
        question: 'What is the guarantor\'s full name?',
        validation: {
          conditionalRequired: {
            dependsOn: 'hasGuarantor',
            condition: { value: 'Yes' }
          }
        }
      },
      {
        id: 'guarantorAddress',
        type: 'text',
        question: 'What is the guarantor\'s address (physical location)?',
        validation: {
          conditionalRequired: {
            dependsOn: 'hasGuarantor',
            condition: { value: 'Yes' }
          }
        }
      },
      {
        id: 'guarantorEmail',
        type: 'text',
        question: 'What is the guarantor\'s email address?',
        validation: {
          conditionalRequired: {
            dependsOn: 'hasGuarantor',
            condition: { value: 'Yes' }
          },
          pattern: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$'
        }
      },
      {
        id: 'isLeasehold',
        type: 'radio',
        question: 'Is the property leasehold with a superior landlord?',
        options: ['Yes', 'No'],
        validation: { required: true }
      },
      {
        id: 'superiorLandlordDetails',
        type: 'text',
        question: 'Provide superior landlord/head lease details:',
        validation: {
          conditionalRequired: {
            dependsOn: 'isLeasehold',
            condition: { value: 'Yes' }
          }
        },
        description: 'Include name and address of the freeholder or head leaseholder.'
      },
      {
        id: 'landlordOccupancy',
        type: 'radio',
        question: 'Will the landlord or family member occasionally occupy the property?',
        options: ['Yes', 'No'],
        validation: { required: true }
      },
      {
        id: 'landlordTenantRelationship',
        type: 'select',
        question: 'Is there any relationship between landlord and tenant?',
        options: ['No relationship', 'Family member', 'Business associate', 'Friend', 'Employee', 'Other'],
        validation: { required: true }
      },
      {
        id: 'rentPaymentMethod',
        type: 'select',
        question: 'How will rent be paid?',
        options: ['Bank Transfer', 'Standing Order', 'Direct Debit', 'Cash', 'Cheque', 'Other'],
        validation: { required: true }
      },
      {
        id: 'rentPaymentFrequency',
        type: 'select',
        question: 'What is the rent payment frequency?',
        options: ['Monthly in advance', 'Weekly', 'Fortnightly', 'Quarterly', 'Other'],
        validation: { required: true }
      },
      {
        id: 'latePaymentFee',
        type: 'number',
        question: 'What is the late payment interest rate (% above Bank of England base rate)?',
        validation: { required: true, min: 0, max: 10 },
        description: 'Typically 3% above BoE base rate. Must be reasonable and reflect actual costs.'
      },
      {
        id: 'utilitiesIncluded',
        type: 'checkbox',
        question: 'Which utilities/services are INCLUDED in the rent?',
        options: ['Gas', 'Electricity', 'Water', 'Council Tax', 'Internet', 'TV License', 'None'],
        validation: { required: true }
      },
      {
        id: 'serviceCharges',
        type: 'radio',
        question: 'Are there any service charges (e.g., for communal areas)?',
        options: ['Yes', 'No'],
        validation: { required: true }
      },
      {
        id: 'serviceChargeAmount',
        type: 'number',
        question: 'What is the monthly service charge amount? (in GBP)',
        validation: {
          conditionalRequired: {
            dependsOn: 'serviceCharges',
            condition: { value: 'Yes' }
          },
          min: 0
        }
      },
      {
        id: 'depositDeductionProcess',
        type: 'text',
        question: 'Describe the process for deposit deductions:',
        validation: { required: true },
        description: 'Specify when deductions may be made (e.g., damages, cleaning, unpaid rent) and dispute resolution process.'
      },
      {
        id: 'gasSafetyCertificate',
        type: 'radio',
        question: 'Has a valid Gas Safety Certificate been obtained?',
        options: ['Yes', 'No', 'No gas supply'],
        validation: { required: true }
      },
      {
        id: 'gasSafetyCertificateDate',
        type: 'date',
        question: 'What is the Gas Safety Certificate issue date?',
        validation: {
          conditionalRequired: {
            dependsOn: 'gasSafetyCertificate',
            condition: { value: 'Yes' }
          }
        }
      },
      {
        id: 'epcRating',
        type: 'select',
        question: 'What is the Energy Performance Certificate (EPC) rating?',
        options: ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
        validation: { required: true },
        description: 'Properties must have a minimum EPC rating of E (with some exceptions).'
      },
      {
        id: 'epcCertificateDate',
        type: 'date',
        question: 'What is the EPC certificate issue date?',
        validation: { required: true }
      },
      {
        id: 'eicrCertificate',
        type: 'radio',
        question: 'Has an Electrical Installation Condition Report (EICR) been obtained?',
        options: ['Yes', 'No'],
        validation: { required: true },
        description: 'Required every 5 years for rental properties in England.'
      },
      {
        id: 'eicrCertificateDate',
        type: 'date',
        question: 'What is the EICR certificate issue date?',
        validation: {
          conditionalRequired: {
            dependsOn: 'eicrCertificate',
            condition: { value: 'Yes' }
          }
        }
      },
      {
        id: 'howToRentProvided',
        type: 'radio',
        question: 'Will the "How to Rent" guide be provided to the tenant?',
        options: ['Yes'],
        validation: { required: true },
        description: 'This is a legal requirement. The guide must be provided at the start of the tenancy.'
      },
      {
        id: 'smokeAlarms',
        type: 'radio',
        question: 'Are smoke alarms installed on each floor?',
        options: ['Yes'],
        validation: { required: true },
        description: 'Legal requirement: working smoke alarms on each storey.'
      },
      {
        id: 'carbonMonoxideAlarms',
        type: 'radio',
        question: 'Are carbon monoxide alarms installed in rooms with solid fuel appliances?',
        options: ['Yes', 'No solid fuel appliances'],
        validation: { required: true }
      },
      {
        id: 'fireEscapeRoutes',
        type: 'text',
        question: 'Describe the fire escape routes:',
        validation: { required: true }
      },
      {
        id: 'selectiveLicensing',
        type: 'radio',
        question: 'Does the local authority require selective licensing for this property?',
        options: ['Yes', 'No', 'Unsure'],
        validation: { required: true },
        description: 'Some local authorities require licenses for all rental properties in certain areas.'
      },
      {
        id: 'selectiveLicenseNumber',
        type: 'text',
        question: 'What is the selective license number?',
        validation: {
          conditionalRequired: {
            dependsOn: 'selectiveLicensing',
            condition: { value: 'Yes' }
          }
        }
      },
      {
        id: 'permittedUse',
        type: 'checkbox',
        question: 'What is the permitted use of the property?',
        options: ['Residential only', 'No business use', 'No subletting', 'No short-term lets (e.g., Airbnb)'],
        validation: { required: true }
      },
      {
        id: 'smokingPolicy',
        type: 'radio',
        question: 'Is smoking permitted in the property?',
        options: ['Yes', 'No', 'Outside only'],
        validation: { required: true }
      },
      {
        id: 'alterationsPolicy',
        type: 'radio',
        question: 'Are alterations and decorating permitted?',
        options: ['Yes with consent', 'No', 'Minor changes only'],
        validation: { required: true }
      },
      {
        id: 'petBondRequired',
        type: 'radio',
        question: 'If pets are allowed, is a pet bond or additional deposit required?',
        options: ['Yes', 'No', 'N/A - No pets'],
        validation: { required: true }
      },
      {
        id: 'repairsResponsibility',
        type: 'text',
        question: 'Specify landlord repair responsibilities:',
        validation: { required: true },
        description: 'Landlord typically responsible for: structure, exterior, heating, sanitation, utilities.'
      },
      {
        id: 'tenantRepairsResponsibility',
        type: 'text',
        question: 'Specify tenant repair responsibilities:',
        validation: { required: true },
        description: 'Tenant typically responsible for: internal decorating, minor repairs, garden maintenance.'
      },
      {
        id: 'landlordInsurance',
        type: 'radio',
        question: 'Does the landlord have buildings insurance?',
        options: ['Yes'],
        validation: { required: true }
      },
      {
        id: 'tenantInsuranceRequired',
        type: 'radio',
        question: 'Is tenant contents insurance required?',
        options: ['Yes - Required', 'No - Recommended only'],
        validation: { required: true }
      },
      {
        id: 'inventoryProcess',
        type: 'text',
        question: 'Describe the inventory and condition reporting process:',
        validation: { required: true },
        description: 'Detail how inventory will be conducted at move-in, during tenancy, and at move-out.'
      },
      {
        id: 'checkOutCleaning',
        type: 'radio',
        question: 'Must the property be professionally cleaned at check-out?',
        options: ['Yes - Professional clean required', 'No - Reasonable cleanliness expected', 'Same condition as move-in'],
        validation: { required: true }
      },
      {
        id: 'knownIssues',
        type: 'text',
        question: 'Are there any known property issues to disclose (e.g., damp, noise, disputes)?',
        validation: { required: false },
        description: 'Legal requirement to disclose known issues that could affect tenant\'s enjoyment of the property.'
      },
      {
        id: 'landlordEntityType',
        type: 'select',
        question: 'Is the landlord an individual or company?',
        options: ['Individual', 'Limited Company', 'Partnership', 'Trust'],
        validation: { required: true }
      },
      {
        id: 'companyNumber',
        type: 'text',
        question: 'What is the company registration number?',
        validation: {
          conditionalRequired: {
            dependsOn: 'landlordEntityType',
            condition: { value: ['Limited Company', 'Partnership'] }
          }
        }
      },
      {
        id: 'dataProtectionConsent',
        type: 'radio',
        question: 'Does the tenant consent to data processing for tenancy purposes (GDPR)?',
        options: ['Yes'],
        validation: { required: true },
        description: 'Data will be processed for: tenancy management, rent collection, maintenance, references, legal obligations.'
      },
      {
        id: 'rightToRentCheck',
        type: 'radio',
        question: 'Has the Right to Rent check been completed?',
        options: ['Yes', 'Pending'],
        validation: { required: true },
        description: 'Landlords must check tenants have the legal right to rent in the UK.'
      }
    ]
  },
  purposeCheck: {
    id: 'purposeCheck',
    title: 'Property Usage',
    fields: [
      {
        id: 'lettingPurpose',
        type: 'select',
        question: 'What is the purpose of this letting?',
        options: ['Holiday Let', 'Employee Accommodation', 'Student Accommodation', 'Temporary Accommodation', 'Other'],
        validation: { required: true },
        nextStep: {
          conditions: [
            { value: 'Holiday Let', goto: 'holidayLet' },
            { value: 'Employee Accommodation', goto: 'employeeLet' }
          ],
          default: 'otherTenancy'
        }
      }
    ]
  },
  companyLet: {
    id: 'companyLet',
    title: 'Company Let Details',
    fields: [
      {
        id: 'companyName',
        type: 'text',
        question: 'What is the full legal name of the company?',
        validation: { required: true }
      },
      {
        id: 'companyRegistrationNumber',
        type: 'text',
        question: 'What is the company registration number?',
        validation: { required: true }
      },
      {
        id: 'registeredAddress',
        type: 'text',
        question: 'What is the registered address of the company?',
        validation: { required: true }
      },
      {
        id: 'signatoryName',
        type: 'text',
        question: 'What is the name of the authorized signatory?',
        validation: { required: true }
      },
      {
        id: 'signatoryPosition',
        type: 'text',
        question: 'What is the position of the authorized signatory?',
        validation: { required: true }
      },
      {
        id: 'propertyAddress',
        type: 'text',
        question: 'What is the full address of the property being let?',
        validation: { required: true }
      },
      {
        id: 'startDate',
        type: 'date',
        question: 'When does the tenancy begin?',
        validation: { required: true }
      },
      {
        id: 'tenancyDuration',
        type: 'select',
        question: 'What is the duration of the tenancy?',
        options: ['6 months', '12 months', '24 months', '36 months', 'Other'],
        validation: { required: true }
      },
      {
        id: 'rentAmount',
        type: 'number',
        question: 'What is the monthly rent amount? (in GBP)',
        validation: { required: true, min: 0 }
      }
    ]
  },
  excludedTenancy: {
    id: 'excludedTenancy',
    title: 'Excluded Tenancy (Lodger) Details',
    fields: [
      {
        id: 'landlordName',
        type: 'text',
        question: 'What is the landlord\'s full name?',
        validation: { required: true }
      },
      {
        id: 'landlordAddress',
        type: 'text',
        question: 'What is the landlord\'s address?',
        validation: { required: true }
      },
      {
        id: 'lodgerName',
        type: 'text',
        question: 'What is the lodger\'s full name?',
        validation: { required: true }
      },
      {
        id: 'lodgerCurrentAddress',
        type: 'text',
        question: 'What is the lodger\'s current address?',
        validation: { required: true }
      },
      {
        id: 'roomDescription',
        type: 'text',
        question: 'Please describe the room/space being let',
        validation: { required: true }
      },
      {
        id: 'sharedAreas',
        type: 'checkbox',
        question: 'Which areas will be shared with the lodger?',
        options: ['Kitchen', 'Bathroom', 'Living Room', 'Garden', 'Other'],
        validation: { required: true }
      },
      {
        id: 'startDate',
        type: 'date',
        question: 'When does the tenancy begin?',
        validation: { required: true }
      },
      {
        id: 'noticePeriod',
        type: 'select',
        question: 'What notice period is required for termination?',
        options: ['7 days', '14 days', '28 days', '1 month'],
        validation: { required: true }
      },
      {
        id: 'rentAmount',
        type: 'number',
        question: 'What is the monthly rent amount? (in GBP)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'rentIncludes',
        type: 'checkbox',
        question: 'What does the rent include?',
        options: ['Council Tax', 'Utilities', 'Internet', 'TV License', 'Cleaning'],
        validation: { required: true }
      }
    ]
  },
  holidayLet: {
    id: 'holidayLet',
    title: 'Holiday Let Agreement',
    fields: [
      {
        id: 'landlordName',
        type: 'text',
        question: 'What is the landlord\'s full name?',
        validation: { required: true }
      },
      {
        id: 'tenantName',
        type: 'text',
        question: 'What is the tenant\'s full name?',
        validation: { required: true }
      },
      {
        id: 'propertyAddress',
        type: 'text',
        question: 'What is the holiday property address?',
        validation: { required: true }
      },
      {
        id: 'startDate',
        type: 'date',
        question: 'What is the start date of the holiday let?',
        validation: { required: true }
      },
      {
        id: 'endDate',
        type: 'date',
        question: 'What is the end date of the holiday let?',
        validation: { required: true }
      },
      {
        id: 'totalRent',
        type: 'number',
        question: 'What is the total rent for the period? (in GBP)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'depositAmount',
        type: 'number',
        question: 'What is the security deposit amount? (in GBP)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'maxOccupants',
        type: 'number',
        question: 'What is the maximum number of occupants?',
        validation: { required: true, min: 1 }
      }
    ]
  },
  employeeLet: {
    id: 'employeeLet',
    title: 'Employee Let Agreement',
    fields: [
      {
        id: 'employerName',
        type: 'text',
        question: 'What is the employer\'s (landlord\'s) full name?',
        validation: { required: true }
      },
      {
        id: 'employeeName',
        type: 'text',
        question: 'What is the employee\'s (tenant\'s) full name?',
        validation: { required: true }
      },
      {
        id: 'employmentRole',
        type: 'text',
        question: 'What is the employee\'s job title/role?',
        validation: { required: true }
      },
      {
        id: 'propertyAddress',
        type: 'text',
        question: 'What is the property address?',
        validation: { required: true }
      },
      {
        id: 'startDate',
        type: 'date',
        question: 'When does the tenancy begin?',
        validation: { required: true }
      },
      {
        id: 'linkToEmployment',
        type: 'radio',
        question: 'Is the tenancy dependent on continued employment?',
        options: ['Yes', 'No'],
        validation: { required: true }
      },
      {
        id: 'rentAmount',
        type: 'number',
        question: 'What is the monthly rent amount? (in GBP)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'rentDeduction',
        type: 'radio',
        question: 'Will the rent be deducted from salary?',
        options: ['Yes', 'No'],
        validation: { required: true }
      }
    ]
  },
  otherTenancy: {
    id: 'otherTenancy',
    title: 'Other Tenancy Agreement',
    fields: [
      {
        id: 'landlordName',
        type: 'text',
        question: 'What is the landlord\'s full name?',
        validation: { required: true }
      },
      {
        id: 'tenantName',
        type: 'text',
        question: 'What is the tenant\'s full name?',
        validation: { required: true }
      },
      {
        id: 'propertyAddress',
        type: 'text',
        question: 'What is the property address?',
        validation: { required: true }
      },
      {
        id: 'purposeDescription',
        type: 'text',
        question: 'Please describe the specific purpose of this tenancy',
        validation: { required: true }
      },
      {
        id: 'startDate',
        type: 'date',
        question: 'When does the tenancy begin?',
        validation: { required: true }
      },
      {
        id: 'endDate',
        type: 'date',
        question: 'When does the tenancy end?',
        validation: { required: true }
      },
      {
        id: 'rentAmount',
        type: 'number',
        question: 'What is the total rent amount? (in GBP)',
        validation: { required: true, min: 0 }
      },
      {
        id: 'specialTerms',
        type: 'text',
        question: 'Are there any special terms or conditions?',
        validation: { required: false }
      }
    ]
  }
};